import { useEffect, useState } from "react";
import api from "../../api/api";
import "./Profile.css";
import BackButton from "../../components/BackButton/BackButton";
import { useGoogleLogin } from "@react-oauth/google";
import ReferralCard from "../../components/Dashboard/ReferralCard";
import ManageSubscriptionModal from "../Upgrade/ManageSubscriptionModal";

/* ── helpers ─────────────────────────────────────────────── */
const fmtDate = (d) =>
  new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

const fmtMinutes = (mins) => {
  if (!mins) return "0 min";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const getBadgeMeta = (badge) => {
  const m = badge.milestone || 0;
  if (m >= 100) return { icon: "🔥", color: "#f59e0b", bg: "rgba(245,158,11,0.08)" };
  if (m >= 50)  return { icon: "⚡", color: "#7c3aed", bg: "rgba(124,58,237,0.08)" };
  return             { icon: "✨", color: "#2563eb", bg: "rgba(37,99,235,0.08)"  };
};

/* ── Component ───────────────────────────────────────────── */
function Profile() {

  const [stats,        setStats]        = useState(null);
  const [certificates, setCertificates] = useState([]);
  const [badges,       setBadges]       = useState([]);
  const [xpData,       setXpData]       = useState(null);
  const [pubProfile,   setPubProfile]   = useState(null);

  const [editing,  setEditing]  = useState(false);
  const [name,     setName]     = useState("");
  const [saving,   setSaving]   = useState(false);
  const [saved,    setSaved]    = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [billingData, setBillingData] = useState(null);

  const login = useGoogleLogin({
    onSuccess: async (codeResponse) => {
      try {
        setSaving(true);
        await api.post('/auth/google/sync-calendar', { code: codeResponse.code });
        // Refresh stats
        const statsRes = await api.get("/profile/stats");
        setStats(statsRes.data);
      } catch (err) {
        console.error('Calendar sync failed', err);
      } finally {
        setSaving(false);
      }
    },
    flow: 'auth-code',
    scope: 'https://www.googleapis.com/auth/calendar.events',
  });

  /* ── exact same fetch as original ────────────────────────── */
  useEffect(() => {
    const fetchData = async () => {
      try {
        const statsRes = await api.get("/profile/stats");
        const certRes  = await api.get("/certificates/my");
        const badgeRes = await api.get("/streak/badges");
        const xpRes    = await api.get("/xp");
        const billRes  = await api.get("/billing/config");
        
        setBillingData(billRes.data);
        
        try {
          let pubRes;
          try {
            pubRes = await api.get("/public-profile/me");
          } catch (e) {
            if (e.response?.status === 404) {
              pubRes = await api.post("/public-profile/setup");
            } else {
              throw e;
            }
          }
          setPubProfile(pubRes.data);
        } catch (e) {
          console.error("Public profile setup failed:", e);
        }

        setStats(statsRes.data);
        setCertificates(certRes.data);
        setBadges(badgeRes.data);
        setXpData(xpRes.data);
        setName(statsRes.data.username);
      } catch (err) {
        console.error(err);
      }
    };
    fetchData();
  }, []);

  /* ── exact same save as original ─────────────────────────── */
  const saveName = async () => {
    try {
      setSaving(true);
      setSaved(false);
      await api.patch("/profile/name", { username: name });
      setStats((prev) => ({ ...prev, username: name }));
      setEditing(false);
      setSaving(false);
      setSaved(true);
    } catch (err) {
      console.error(err);
      setSaving(false);
    }
  };

  /* ── loading ─────────────────────────────────────────────── */
  if (!stats) {
    return (
      <div className="pf-loading">
        <div className="pf-spinner" />
        <p>Loading profile…</p>
      </div>
    );
  }

  /* ── derived ─────────────────────────────────────────────── */
  const xpPercent = xpData ? Math.min(xpData.percent, 100) : 0;

  const STATS = [
    { icon: "📚", label: "Active Courses",    value: stats.activeCourses    ?? 0 },
    { icon: "✅", label: "Completed Courses", value: stats.completedCourses ?? 0 },
    { icon: "⏱",  label: "Focus Time",        value: fmtMinutes(stats.totalMinutes) },
    { icon: "🔥", label: "Current Streak",    value: `${stats.streak ?? 0} days` },
    { icon: "🎯", label: "Level",             value: xpData?.level ?? stats.level ?? 1 },
    { icon: "⭐", label: "Total XP",          value: xpData?.totalXp ?? stats.xp ?? 0 },
  ];

  /* ── render ──────────────────────────────────────────────── */
  return (
    <div className="pf-page">
      <div className="pf-container">

        <div className="pf-back-row">
          <BackButton to="/dashboard" label="Dashboard" />
        </div>

        {/* ══ Hero card ══════════════════════════════════════ */}
        <div className="pf-hero">
          <div className="pf-hero-bg" />

          {/* Avatar */}
          <div className="pf-avatar">
            {stats.avatar ? (
              <img src={stats.avatar} alt={stats.username} className="pf-avatar-img" />
            ) : (
              <span>{(stats.username || "U")[0].toUpperCase()}</span>
            )}
            <div className="pf-avatar-glow" />
          </div>

          {/* Name */}
          {!editing ? (
            <div className="pf-name-view">
              <h1 className="pf-name">{stats.username}</h1>
              {stats.subscription?.status === "ACTIVE" && (
                <span className="pro-badge-pill" title="Learnlia PRO member">PRO</span>
              )}
              <button
                className="pf-edit-icon-btn"
                onClick={() => { setEditing(true); setSaved(false); }}
                title="Edit name"
              >✏</button>
            </div>
          ) : (
            <div className="pf-name-edit">
              <input
                className="pf-name-input"
                value={name}
                maxLength={40}
                autoFocus
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter")  saveName();
                  if (e.key === "Escape") { setEditing(false); setName(stats.username); }
                }}
                placeholder="Your name…"
              />
              <p className="pf-name-note">This name appears on your certificates</p>
              <div className="pf-name-btns">
                <button className="pf-save-btn" onClick={saveName} disabled={saving}>
                  {saving ? "Saving…" : "Save"}
                </button>
                <button
                  className="pf-cancel-btn"
                  onClick={() => { setEditing(false); setName(stats.username); }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {saved && <span className="pf-saved-msg">✓ Name updated</span>}

          {/* XP bar */}
          <div className="pf-xp-wrap">
            <div className="pf-xp-row">
              <span className="pf-xp-level">Level {xpData?.level ?? stats.level ?? 1}</span>
              <span className="pf-xp-count">
                {xpData ? `${xpData.xpIntoLevel} / ${xpData.xpForNext} XP` : `${stats.xp ?? 0} XP`}
              </span>
            </div>
            <div className="pf-xp-track">
              <div className="pf-xp-fill" style={{ width: `${xpPercent}%` }} />
            </div>
            {xpData && (
              <span className="pf-xp-hint">
                {xpData.xpForNext - xpData.xpIntoLevel} XP to level {xpData.level + 1}
              </span>
            )}
          </div>
        </div>

        {/* ══ Goals Section ════════════════════════════════════ */}
        {stats.onboarding?.completed && (
          <div className="pf-section pf-goals-card">
            <div className="pf-section-head">
              <h2>My Learning Strategy</h2>
            </div>
            <div className="pf-goals-grid">
              <div className="pf-goal-item">
                <span className="pf-goal-label">🎯 Target Role</span>
                <span className="pf-goal-val">{stats.onboarding.targetRole || "Not set"}</span>
              </div>
              <div className="pf-goal-item">
                <span className="pf-goal-label">🚀 Focus Area</span>
                <span className="pf-goal-val">{stats.onboarding.focusArea || "Not set"}</span>
              </div>
              <div className="pf-goal-item">
                <span className="pf-goal-label">📅 Goal Date</span>
                <span className="pf-goal-val">{stats.onboarding.targetMonth || "Not set"}</span>
              </div>
              <div className="pf-goal-item">
                <span className="pf-goal-label">⚡ Experience</span>
                <span className="pf-goal-val" style={{textTransform: 'capitalize'}}>{stats.onboarding.experienceLevel}</span>
              </div>
              <div className="pf-goal-item">
                <span className="pf-goal-label">⏰ Daily Commitment</span>
                <span className="pf-goal-val">{stats.onboarding.dailyCommitment || "Not set"}</span>
              </div>
            </div>
          </div>
        )}

        {/* ══ Public Link Section ════════════════════════════════ */}
        {pubProfile && (
           <div className="pf-section pf-public-box">
             <div className="pf-public-info">
               <h3>Public Profile URL</h3>
               <p>Share this link with recruiters or on LinkedIn to show your verified progress.</p>
               <div className="pf-url-row">
                 <code className="pf-url-text">learnlia.com/u/{pubProfile.username}</code>
                 <button 
                   className="pf-copy-btn" 
                   onClick={() => {
                     navigator.clipboard.writeText(`https://learnlia.com/u/${pubProfile.username}`);
                     alert("Link copied!");
                   }}
                 >
                   Copy
                 </button>
               </div>
               <label className="pf-toggle-label">
                 <input 
                   type="checkbox" 
                   checked={pubProfile.isPublic}
                   onChange={async (e) => {
                     const updated = await api.patch("/public-profile/me", { isPublic: e.target.checked });
                     setPubProfile(updated.data);
                   }}
                 />
                 Private Profile (Hide from search & recruiters)
               </label>
             </div>
           </div>
        )}

        {/* ══ Referral Section ══════════════════════════════════ */}
        <div className="pf-section" id="referral">
          <div className="pf-section-head">
            <h2>Invite & Earn</h2>
          </div>
          <ReferralCard referralCode={stats.referralCode} />
          <div className="pf-ref-instructions">
            <p style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '12px', lineHeight: '1.5' }}>
              Share your link with friends. When they join and complete Phase 1, you'll earn <strong>30 days of PRO access</strong> for free. It’s the easiest way to unlock premium features and AI quizzes!
            </p>
          </div>
        </div>

        {/* ══ Connected Services ════════════════════════════ */}
        <div className="pf-section pf-services-card">
          <div className="pf-section-head">
            <h2>Connected Services</h2>
          </div>
          <div className="pf-service-card">
            <div className="pf-service-info">
              <div className="pf-service-icon">🗓️</div>
              <div className="pf-service-details">
                <h4>Google Calendar</h4>
                <p>{stats.calendarEventId ? "Study sessions are scheduled in your calendar." : "Automatically schedule daily study reminders."}</p>
              </div>
            </div>
            <button 
              className={`pf-service-btn ${stats.calendarEventId ? 'connected' : ''}`}
              onClick={() => !stats.calendarEventId && login()}
              disabled={stats.calendarEventId || saving}
            >
              {saving ? "Syncing..." : stats.calendarEventId ? "Connected" : "Connect Calendar"}
            </button>
          </div>
        </div>

        {/* ══ Stats grid ══════════════════════════════════════ */}
        <div className="pf-stats-grid">
          {STATS.map((s, i) => (
            <div
              key={s.label}
              className="pf-stat"
              style={{ animationDelay: `${i * 0.06}s` }}
            >
              <div className="pf-stat-icon">{s.icon}</div>
              <div className="pf-stat-val">{s.value}</div>
              <div className="pf-stat-label">{s.label}</div>
            </div>
          ))}
        </div>

        {/* ══ Badges ══════════════════════════════════════════ */}
        <div className="pf-section">
          <div className="pf-section-head">
            <h2>Badges</h2>
            {badges.length > 0 && (
              <span className="pf-badge-count">{badges.length}</span>
            )}
          </div>

          {badges.length === 0 ? (
            <div className="pf-empty">
              <span className="pf-empty-icon">🏅</span>
              <p>Keep your streak alive to earn badges!</p>
            </div>
          ) : (
            <div className="pf-badges-grid">
              {badges.map((badge, i) => {
                const meta = getBadgeMeta(badge);
                return (
                  <div
                    key={badge._id}
                    className="pf-badge"
                    style={{
                      "--bc": meta.color,
                      "--bb": meta.bg,
                      animationDelay: `${i * 0.07}s`,
                    }}
                  >
                    <div className="pf-badge-icon">{meta.icon}</div>
                    <div className="pf-badge-name">{badge.name || `${badge.milestone}-Day Streak`}</div>
                    {badge.milestone && (
                      <div className="pf-badge-pill">{badge.milestone} days</div>
                    )}
                    <div className="pf-badge-date">{fmtDate(badge.earnedAt)}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ══ Certificates ════════════════════════════════════ */}
        <div className="pf-section">
          <div className="pf-section-head">
            <h2>Certificates</h2>
          </div>

          {certificates.length === 0 ? (
            <div className="pf-empty">
              <span className="pf-empty-icon">🏆</span>
              <p>Complete a learning track to earn your first certificate.</p>
            </div>
          ) : (
            <div className="pf-certs-grid">
              {certificates.map((cert) => (
                <div
                  key={cert.certificateId}
                  className="pf-cert"
                  onClick={() => window.open(`/certificate/${cert.certificateId}`, "_blank")}
                >
                  <div className="pf-cert-icon">🏆</div>
                  <div className="pf-cert-info">
                    <div className="pf-cert-name">{cert.trackName}</div>
                    <div className="pf-cert-date">{fmtDate(cert.issuedAt)}</div>
                  </div>
                  <div className="pf-cert-arrow">↗</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ══ Subscription Management ════════════════════════ */}
        {stats.subscription?.status === "ACTIVE" && (
          <div className="pf-section pf-manage-box">
            <div className="pf-section-head">
               <h2>Subscription</h2>
            </div>
            <div className="pf-manage-card">
              <div className="pf-manage-info">
                 <p>You are currently on the <strong>PRO Plan</strong>. Your next billing date is <strong>{fmtDate(stats.subscription.currentPeriodEnd)}</strong>.</p>
              </div>
              <button className="pf-manage-btn" onClick={() => setIsModalOpen(true)}>
                Manage Subscription
              </button>
            </div>
          </div>
        )}

        <ManageSubscriptionModal 
          isOpen={isModalOpen} 
          onClose={() => setIsModalOpen(false)} 
          subscription={billingData?.subscription}
          onUpdate={async () => {
             const res = await api.get("/billing/config");
             setBillingData(res.data);
             // Refresh stats too
             const statsRes = await api.get("/profile/stats");
             setStats(statsRes.data);
          }}
        />

      </div>
    </div>
  );
}

export default Profile;